import { useState } from "react";
import { CreditCard, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtD } from "@/lib/api";
import { PageHeader, Card, Pill } from "@/components/kit";

function PackageCard({ p, selected, current, onPick, venues }) {
  return (
    <button type="button" data-testid={`package-option-${p.id}`} onClick={() => onPick(p.id)} className={`ka-press flex flex-col items-stretch justify-start rounded-2xl border-2 bg-white p-5 text-left transition-colors ${selected ? "border-emerald-600" : "border-slate-200 hover:border-slate-400"}`}>
      <div className="flex items-start justify-between gap-2"><div className="font-display text-lg font-extrabold">{p.name}{current && <span data-testid={`package-current-${p.id}`} className="ml-2 rounded-full bg-slate-900 px-2 py-0.5 align-middle font-mono text-[10px] uppercase tracking-wider text-white">Current</span>}</div>{selected && <Check className="h-5 w-5 text-emerald-600" />}</div>
      <div className="mt-1 text-2xl font-extrabold">£{p.price_per_venue.toFixed(2)}<span className="text-sm font-medium text-slate-500"> / venue / month</span></div>
      {p.description && <p className="mt-2 text-sm text-slate-600">{p.description}</p>}
      <ul className="mt-3 space-y-1 text-sm text-slate-600">{p.features.map((f) => <li key={f} className="flex gap-1.5"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{f}</li>)}</ul>
      <div className="mt-3 text-xs text-slate-500">{venues} venue(s) = £{(p.price_per_venue * venues).toFixed(2)} / month</div>
    </button>
  );
}

function CouponBox({ coupon, setCoupon, onRedeemed, saved }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/billing/coupon/check", { code });
      if (data.kind === "free") {
        const { data: r } = await api.post("/billing/redeem", { code });
        toast.success(`Code applied: ${r.summary}. You're all set until ${fmtD(r.paid_until)}.`);
        setCode("");
        onRedeemed();
      } else {
        setCoupon(data);
        toast.success(`Code applied: ${data.summary}`);
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (coupon) return <div data-testid="coupon-applied" className="mt-5 flex items-center justify-between gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900 ring-1 ring-emerald-200"><span><b className="font-mono">{coupon.code}</b> · {coupon.summary}. {saved ? "Saved from your sign-up, " : ""}Stripe takes it off at checkout.</span><button data-testid="coupon-remove" onClick={() => setCoupon(null)} className="text-xs font-semibold underline">Remove</button></div>;
  return (
    <form onSubmit={apply} className="mt-5 flex gap-2">
      <input data-testid="coupon-input" required maxLength={30} className="ka-input flex-1 font-mono uppercase" placeholder="Have a code?" value={code} onChange={(e) => setCode(e.target.value)} />
      <button data-testid="coupon-apply" disabled={busy} className="ka-press rounded-full border border-slate-300 px-4 text-sm font-semibold hover:border-slate-900 disabled:opacity-60">{busy ? "Checking…" : "Apply"}</button>
    </form>
  );
}

export default function Billing() {
  const { data, reload } = useApi("/billing");
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState(null);
  const [picked, setCoupon] = useState(undefined);
  if (!data) return null;
  const coupon = picked === undefined ? data.pending_coupon : picked;
  const active = data.subscription_status === "active";
  const freeCode = active && data.payment_method === "free_code";
  const canSubscribe = !active || freeCode;
  const chosen = data.packages.find((p) => p.id === (pick || data.package?.id)) || data.package;
  const venues = Math.max(1, data.venues);
  const price = chosen?.price_per_venue || 0;
  const switching = active && chosen && data.package && chosen.id !== data.package.id;

  const switchPackage = async () => {
    setBusy(true);
    try {
      await api.post("/billing/package", { package_id: chosen.id });
      toast.success(`Switched to ${chosen.name}. The new price applies from your next bill.`);
      setPick(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data: s } = await api.post("/payments/checkout", { origin_url: window.location.origin, package_id: chosen?.id, coupon_code: coupon?.code || null });
      window.location.href = s.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Subscription" title="Billing" subtitle="Priced per venue per month. Staff don't take a seat." />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="ka-eyebrow">Status</div>
          <div className="mt-2 flex items-center gap-3"><Pill testid="billing-status" status={active ? "ok" : "due"} label={data.subscription_status} /></div>
          {data.subscription_status === "trialing" && <p className="mt-3 text-sm text-slate-600">Free trial ends <b>{fmtD(data.trial_ends_at)}</b>.</p>}
          {active && <p className="mt-3 text-sm text-slate-600">{freeCode ? <>Free access for <b>{data.paid_venues}</b> venue(s) until <b>{fmtD(data.paid_until)}</b> · free access code</> : <>Paid for <b>{data.paid_venues}</b> venue(s){data.paid_until && <> until <b>{fmtD(data.paid_until)}</b></>}{data.payment_method === "manual" && " · paid offline"}</>}.</p>}
        </Card>
        <Card>
          <div className="ka-eyebrow">{switching ? "New plan (preview)" : "Your plan"}{chosen && ` · ${chosen.name}`}</div>
          <div className="mt-2 font-display text-5xl font-extrabold" data-testid="billing-total">£{(venues * price).toFixed(2)}<span className="text-base font-medium text-slate-500"> / month</span></div>
          <p className="mt-1 text-sm text-slate-500">{venues} venue(s) × £{price.toFixed(2)}</p>
          {canSubscribe && chosen && !freeCode && <CouponBox coupon={coupon} saved={picked === undefined && !!data.pending_coupon} setCoupon={setCoupon} onRedeemed={reload} />}
          {freeCode && <p data-testid="free-code-note" className="mt-5 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-900 ring-1 ring-emerald-200">You're on free access until <b>{fmtD(data.paid_until)}</b>. Add a card now and your first payment is taken when the free period ends.</p>}
          {canSubscribe && chosen && (
            <button data-testid="subscribe-button" disabled={busy} onClick={subscribe} className="ka-press mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"><CreditCard className="h-4 w-4" />{busy ? "Opening checkout…" : `Subscribe to ${chosen.name}`}</button>
          )}
          {switching && (
            <div data-testid="package-switch-panel" className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
              <p>Switch from <b>{data.package.name}</b> (£{data.package.price_per_venue.toFixed(2)}) to <b>{chosen.name}</b> (£{chosen.price_per_venue.toFixed(2)}) per venue. No part-month charge; the new price starts from your next bill.</p>
              <div className="mt-3 flex gap-2">
                <button data-testid="confirm-package-switch-button" disabled={busy} onClick={switchPackage} className="ka-press rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy ? "Switching…" : `Switch to ${chosen.name}`}</button>
                <button data-testid="cancel-package-switch-button" onClick={() => setPick(null)} className="ka-press rounded-full px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900">Keep current</button>
              </div>
            </div>
          )}
          {active && data.paid_venues < data.venues && <p className="mt-4 text-sm text-amber-700">You've added venues since subscribing — contact support to update your plan.</p>}
        </Card>
      </div>
      {data.packages.length > 1 && (
        <div className="mt-8">
          <div className="ka-eyebrow mb-3">{active ? "Change package" : "Choose a package"}</div>
          <div data-testid="package-options" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.packages.map((p) => <PackageCard key={p.id} p={p} venues={venues} selected={p.id === chosen?.id} current={active && p.id === data.package?.id} onPick={setPick} />)}
          </div>
        </div>
      )}
    </div>
  );
}
